<template>
    <div
        class="flex cursor-pointer items-center gap-4 border p-4 hover:border-primary-500"
        :class="selected ? 'border-primary-800' : ''"
        :style="[
            selected
                ? 'box-shadow: 0 0 0 1px rgb(var(--color-primary-500));'
                : ''
        ]"
        @click="$emit('select')"
    >
        <radio-button
            :model-value="selected ? courseData.id : null"
            :input-id="courseData.id"
            :name="courseData.publicTitle"
            :aria-label="courseData.publicTitle"
            :value="courseData.id"
            @update:model-value="$event ? $emit('select') : void 0"
        />

        <div class="flex-1 space-y-1">
            <div class="flex gap-4">
                <div class="flex-1">
                    <label
                        :for="courseData.id"
                        class="text-lg font-semibold"
                    >
                        {{ courseData.publicTitle }}
                    </label>
                    <div
                        v-if="courseData.subtitle !== ''"
                        class="pb-2 text-sm"
                    >
                        {{ courseData.subtitle }}
                    </div>
                </div>
                <div
                    :class="
                        showRedMessage ? 'text-red-600 font-medium' : 'italic'
                    "
                >
                    {{ courseAvailabilityMessage }}
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { graphql } from '~/resources/graphql'

export const RecurringCourseRadioButtonCourseFragment = graphql(`
    fragment RecurringCourseRadioButtonCourseFragment on Course {
        id
        publicTitle
        subtitle
        capacityResult(filter: $capacityResultFilter) {
            enrollments
            overlappedBookedSessions
            contracts
        }
        bookingPeriodAvailable(customerId: null, bookingDate: null)
    }
`)
</script>

<script setup lang="ts">
import RadioButton from 'primevue/radiobutton'
import { computed } from 'vue'
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData } from '~/resources/graphql'
import { UNLIMITED_CAPACITY } from '~/components/PurchaseContract/Program/course-capacity'

const props = defineProps<{
    course: FragmentType<typeof RecurringCourseRadioButtonCourseFragment>
    selected: boolean
    availabilityThreshold: number | null
}>()

defineEmits<{
    (e: 'select'): void
}>()

const courseData = computed(() =>
    getFragmentData(RecurringCourseRadioButtonCourseFragment, props.course)
)

const courseAvailabilityMessage = computed(() => {
    // If the asset schedule isn't allowing more sessions to be booked, you can't book
    if (courseData.value.capacityResult.overlappedBookedSessions <= 0) {
        return 'Course is full.'
    }

    // If no more contracts are allowed to be booked
    if (courseData.value.capacityResult.contracts <= 0) {
        return 'Course is full.'
    }

    // If available enrollments is 0, return full
    const enrollmentCapacity = courseData.value.capacityResult.enrollments
    if (enrollmentCapacity <= 0) {
        return 'Course is full.'
    }

    // If our booking period is not available, return booking period message
    if (courseData.value.bookingPeriodAvailable === 'BEFORE_BOOKING_PERIOD') {
        return 'NOT AVAILABLE YET'
    } else if (
        courseData.value.bookingPeriodAvailable === 'AFTER_BOOKING_PERIOD'
    ) {
        return 'Booking period closed.'
    }

    // --Course Available--
    // If we have more capacity than the availability display threshold, or there is unlimited capacity
    if (
        (props.availabilityThreshold !== null &&
            enrollmentCapacity > props.availabilityThreshold) ||
        enrollmentCapacity === UNLIMITED_CAPACITY
    ) {
        return 'Available'
    }

    if (enrollmentCapacity > 1) {
        return enrollmentCapacity + ' spaces available'
    } else if (enrollmentCapacity === 1) {
        return '1 space available'
    } else {
        throw new Error()
    }
})

const showRedMessage = computed(
    () =>
        courseAvailabilityMessage.value === 'Course is full.' ||
        courseAvailabilityMessage.value === 'Booking period closed.'
)
</script>
